/**
 * Admin Page
 *
 * Admin panel with tabs for managing:
 * - Clients (super_admin only)
 * - Reports
 * - Users/Viewers
 *
 * Role-based access:
 * - super_admin: Clients, Reports, Users (all types)
 * - admin: Reports, Viewers (own client only)
 */
import { useState, useMemo } from 'react';
import { AdminProvider, useAdmin } from '../contexts/AdminContext';
import { useAuth } from '../contexts/AuthContext';
import { ClientsManager, ReportsManager, UsersManager } from '../components/admin';

type TabType = 'clients' | 'reports' | 'users';

const AdminContent = () => {
  const { isLoading, error } = useAdmin();
  const { isSuperAdmin } = useAuth();

  // Build tabs based on role
  const tabs = useMemo(() => {
    if (isSuperAdmin) {
      return [
        { id: 'clients' as TabType, label: 'Clients' },
        { id: 'reports' as TabType, label: 'Reports' },
        { id: 'users' as TabType, label: 'Users' },
      ];
    }
    // Admin - no Clients tab, Users renamed to Viewers
    return [
      { id: 'reports' as TabType, label: 'Reports' },
      { id: 'users' as TabType, label: 'Viewers' },
    ];
  }, [isSuperAdmin]);

  // Default tab: clients for super_admin, reports for admin
  const [activeTab, setActiveTab] = useState<TabType>(isSuperAdmin ? 'clients' : 'reports');

  // Show loading spinner while initial data loads
  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <svg
            className="animate-spin h-8 w-8 text-metrica-green"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <span className="text-gray-600">Loading admin panel...</span>
        </div>
      </div>
    );
  }

  // Show error if data loading failed
  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Failed to load admin panel
          </h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 text-sm font-medium text-white bg-metrica-green rounded-lg hover:bg-metrica-green/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6">
          <nav className="flex gap-4" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-4 py-3 text-sm font-medium border-b-2 transition-colors
                  ${activeTab === tab.id
                    ? 'border-metrica-green text-metrica-green'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-6 bg-gray-50 overflow-auto">
        <div className="max-w-6xl">
          {activeTab === 'clients' && <ClientsManager />}
          {activeTab === 'reports' && <ReportsManager />}
          {activeTab === 'users' && <UsersManager />}
        </div>
      </div>
    </div>
  );
};

export const AdminPage = () => {
  return (
    <AdminProvider>
      <AdminContent />
    </AdminProvider>
  );
};

export default AdminPage;
